import React, { useContext, useRef, useEffect, useState } from "react";
import SidePopup from "../../Common/SidePopup/SidePopup";
import { DynamicContext } from "../../Context/DynamicContext";
import feedbackaloneIcon from "../../Assets/Images/feedback-alone-dark-icon.png";
import FeedBackDarkIcon from "../../Assets/Images/feedback-dark-icon.png";
import TextArea from "../../Common/TextArea/TextArea";
import * as StyleDOM from "../Applicant-Tracking/Candidates/CandidateDetails/CandidateDocuments.jsx";
import {
  CustomDragAndDrop,
  handleFileSelect,
} from "../../Common/Common-Function/CommonFunctions";
import { Toast } from "../../Common/Toast-Snackbar/ToastSnackbar";
import FileDropIcon from "../../Assets/Images/upload.svg";
import Button from "../../Common/Button/Button";
import "./FeedBack.css";
import { useFormik } from "formik";
import * as Yup from "yup";
import { ReactComponent as DeleteIcon } from "../../Assets/Images/trash.svg";
import DynamicRating from "../../Common/Rating/Rating";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import { VYLABS_BASE_URL, SEND_FEEDBACK } from "../../helper/url_helper.js";
import { post } from "../../helper/Api_helper.js";

function Feedback() {
  const {
    OpenFeedBack,
    setOpenFeedBack,
    isDarkMode,
    feedbackScreenshot,
  } = useContext(DynamicContext);
  const ref = useRef(null);
  const [screenshotAdded, setScreenshotAdded] = useState(false);
  const [screenshotDeleted, setScreenshotDeleted] = useState(false);

  const validationSchema = Yup.object({
    feedbackContent: Yup.string().required("Feedback description is required"),
    feedbackSubject: Yup.string().optional(),
    overallRatings: Yup.number().optional(),
    feedbackImages: Yup.array().optional(),
  });

  const formik = useFormik({
    initialValues: {
      overallRatings: 0,
      feedbackContent: "",
      feedbackImages: [],
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        const formData = new FormData();
        formData.append("feedback_desc", values.feedbackContent);
        formData.append("ratings", values.overallRatings);
        values.feedbackImages.forEach((fileData) => {
          formData.append("files", fileData.file);
        });
        const responseData = await post(
          VYLABS_BASE_URL,
          SEND_FEEDBACK,
          formData,
        );
        if (responseData && responseData.key) {
          Toast("success", "Feedback submitted successfully!");
          setOpenFeedBack(false);
          formik.resetForm({
            values: {
              overallRatings: 0,
              feedbackContent: "",
              feedbackImages: [],
            },
          });
          setScreenshotAdded(false);
          setScreenshotDeleted(false);
        } else {
          Toast(
            "error",
            "Failed to submit feedback: Unexpected response format",
          );
        }
      } catch (error) {
        console.error("Error submitting feedback:", error);
        Toast("error", error.message || "Failed to submit feedback");
      }
    },
  });
  useEffect(() => {
    if (
      OpenFeedBack &&
      feedbackScreenshot &&
      !screenshotAdded &&
      !screenshotDeleted
    ) {
      fetch(feedbackScreenshot)
        .then((res) => res.blob())
        .then((blob) => {
          const file = new File([blob], "screenshot.png", {
            type: "image/png",
          });
          const fileData = { name: "screenshot.png", file };
          formik.setFieldValue("feedbackImages", [fileData]);
          setScreenshotAdded(true);
        })
        .catch((error) => {
          console.error("Error converting screenshot to file:", error);
          Toast("error", "Failed to add screenshot to uploads.");
        });
    }
  }, [OpenFeedBack, feedbackScreenshot, screenshotAdded, screenshotDeleted]);

  useEffect(() => {
    if (OpenFeedBack) {
      formik.resetForm({
        values: { overallRatings: 0, feedbackContent: "", feedbackImages: [] },
      });
      setScreenshotAdded(false);
      setScreenshotDeleted(false);
    }
  }, [OpenFeedBack]);

  const handleSubmit = async () => {
    const errors = await formik.validateForm();
    if (Object.keys(errors).length === 0) {
      formik.handleSubmit();
    } else {
      formik.setTouched({
        feedbackContent: true,
      });
      Toast("error", "Please fill all required fields correctly.");
    }
  };

  const closeFeedBackModal = () => {
    setOpenFeedBack(false);
    formik.resetForm({
      values: { overallRatings: 0, feedbackContent: "", feedbackImages: [] },
    });
    setScreenshotAdded(false);
    setScreenshotDeleted(false);
  };

  const AllowedFiles = (files) => {
    const validFiles = handleFileSelect(files, [".jpeg", ".jpg", ".png"], 1);
    if (validFiles.length > 0) {
      const newFilesData = validFiles.map((file) => ({
        name: file.name,
        file: file,
      }));
      return newFilesData;
    } else {
      Toast(
        "warning",
        "Please upload files with valid formats: JPEG, PNG (max 1MB).",
      );
      return [];
    }
  };

  const handleDropFiles = (event) => {
    event.preventDefault();
    const files = Array.from(event.dataTransfer.files);
    const newFilesData = AllowedFiles(files);
    if (newFilesData.length > 0) {
      formik.setFieldValue("feedbackImages", [
        ...formik.values.feedbackImages,
        ...newFilesData,
      ]);
    }
  };

  const handleFileInputChange = (e) => {
    const files = Array.from(e.target.files);
    const newFilesData = AllowedFiles(files);
    if (newFilesData.length > 0) {
      formik.setFieldValue("feedbackImages", [
        ...formik.values.feedbackImages,
        ...newFilesData,
      ]);
    }
  };

  const handleFileUpload = () => {
    ref.current.click();
  };

  const handleDelete = (fileName) => {
    formik.setFieldValue(
      "feedbackImages",
      formik.values.feedbackImages.filter((file) => file.name !== fileName),
    );
    if (fileName === "screenshot.png") {
      setScreenshotAdded(false);
      setScreenshotDeleted(true);
    }
  };

  const { isDragging, handleDragEnter, handleDragOver, handleDragLeave } =
    CustomDragAndDrop(handleDropFiles, false);

  return (
    <div>
      <SidePopup
        isOpen={OpenFeedBack}
        onClose={closeFeedBackModal}
        title="Share Your Feedback"
        showDeleteButton={false}
        direction="down"
        headerTitleIcon={true}
        headerTitleIconSrc={isDarkMode ? FeedBackDarkIcon : feedbackaloneIcon}
        subTitle="Help us improve by sharing your experience and suggestions"
        titleIconWidth="25px"
      >
        <div className="feedback-main-container">
          <div className="feedback-input-container">
            {feedbackScreenshot &&
              formik.values.feedbackImages[0]?.name === "screenshot.png" && (
                <div className="feedback-inner-container">
                  <div className="screen-shot-container">
                    <div className="screen-shot-title-and-imag">
                      <h3>Screenshot Preview</h3>
                      <img
                        src={feedbackScreenshot}
                        alt="Page Screenshot"
                        className="screen-shot-image"
                      />
                    </div>
                  </div>
                </div>
              )}
            <div className="feedback-inner-container">
              <DynamicRating
                name="overallRatings"
                value={formik.values.overallRatings}
                max={5}
                precision={1}
                size="medium"
                label="Ratings"
                filledIcon={<StarIcon fontSize="inherit" />}
                emptyIcon={<StarBorderIcon fontSize="inherit" />}
                filledColor="#ffd700"
                hoverColor="#ffc107"
                emptyColor={isDarkMode ? "#ffffff" : "#ccc"}
                onChange={(event, newValue) => {
                  formik.setFieldValue("overallRatings", newValue || 0);
                }}
              />
            </div>
            <div className="feedback-inner-container">
              <TextArea
                required={true}
                placeholder="Please describe your feedback here.."
                className="ai-search-textfield"
                name="feedbackContent"
                label="Your Feedback"
                value={formik.values.feedbackContent}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.feedbackContent &&
                  formik.errors.feedbackContent
                }
                errorMessage={
                  formik.touched.feedbackContent &&
                  formik.errors.feedbackContent
                }
              />
            </div>
            <div className="feedback-inner-container">
              <StyleDOM.DocumentLeftContainer className="documentleft-container">
                {formik.values.feedbackImages.length === 0 ? (
                  <StyleDOM.FileDropMainContainer style={{ width: "100%" }}>
                    <StyleDOM.FileDropContainer
                      onClick={handleFileUpload}
                      onDragEnter={handleDragEnter}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDropFiles}
                      isDragging={isDragging}
                    >
                      <img src={FileDropIcon} alt="File-uploadicon" />
                      <StyleDOM.FileDropMessage>
                        {isDragging
                          ? "Drop your files here"
                          : "Drag and Drop your files"}
                      </StyleDOM.FileDropMessage>
                      <StyleDOM.NoteListContainer>
                        <StyleDOM.NoteList>
                          The file size should not exceed 1 mb
                        </StyleDOM.NoteList>
                        <StyleDOM.NoteList>
                          Supported Formats: JPEG, PNG
                        </StyleDOM.NoteList>
                      </StyleDOM.NoteListContainer>
                      <input
                        accept=".jpeg,.jpg,.png"
                        name="fileName"
                        type="file"
                        ref={ref}
                        style={{ display: "none" }}
                        onChange={handleFileInputChange}
                      />
                    </StyleDOM.FileDropContainer>
                  </StyleDOM.FileDropMainContainer>
                ) : (
                  <>
                    <StyleDOM.FileDropMainContainer style={{ width: "80%" }}>
                      <StyleDOM.FileDropContainer
                        onClick={handleFileUpload}
                        onDragEnter={handleDragEnter}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDropFiles}
                        isDragging={isDragging}
                      >
                        <img src={FileDropIcon} alt="File-uploadicon" />
                        <StyleDOM.FileDropMessage>
                          {isDragging
                            ? "Drop your files here"
                            : "Drag and Drop your files"}
                        </StyleDOM.FileDropMessage>
                        <StyleDOM.NoteListContainer>
                          <StyleDOM.NoteList>
                            The file size should not exceed 1 mb
                          </StyleDOM.NoteList>
                          <StyleDOM.NoteList>
                            Supported Formats: JPEG, PNG
                          </StyleDOM.NoteList>
                        </StyleDOM.NoteListContainer>
                        <input
                          accept=".jpeg,.jpg,.png"
                          name="fileName"
                          type="file"
                          ref={ref}
                          style={{ display: "none" }}
                          onChange={handleFileInputChange}
                        />
                      </StyleDOM.FileDropContainer>
                    </StyleDOM.FileDropMainContainer>
                    <StyleDOM.UploadedFilesContainer style={{ width: "20%" }}>
                      {formik.values.feedbackImages.map((file, index) => (
                        <StyleDOM.UploadedFileItem key={index}>
                          <StyleDOM.FileNameContainer>
                            {file.name}
                          </StyleDOM.FileNameContainer>
                          <StyleDOM.DeleteIconDiv
                            onClick={() => handleDelete(file.name)}
                          >
                            <DeleteIcon />
                          </StyleDOM.DeleteIconDiv>
                        </StyleDOM.UploadedFileItem>
                      ))}
                    </StyleDOM.UploadedFilesContainer>
                  </>
                )}
              </StyleDOM.DocumentLeftContainer>
            </div>
            <div className="feedback-button-container">
              <Button
                label="Cancel"
                isCancel={true}
                event={closeFeedBackModal}
              />
              <Button
                label="Send Feedback"
                type="button"
                event={handleSubmit}
              />
            </div>
          </div>
        </div>
      </SidePopup>
    </div>
  );
}

export default Feedback;
